import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarPlus,
  faChevronDown,
  faChevronUp,
  faPenToSquare,
  faSpinnerThird,
} from "@fortawesome/pro-regular-svg-icons";
import { addDays, differenceInMinutes, endOfDay, format, isSameDay, startOfDay } from "date-fns";
import { getUiDateLocale } from "../../helpers/DateTime";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import LoadingSpinner from "../../components/LoadingSpinner";
import BookingLegend, { isTimelineBookingWithoutBuffer } from "./BookingLegend";
import BookingFilters from "./BookingFilters";
import SiteCard from "./BookingTimeline";
import BookingWizardModal from "./BookingWizardModal";
import { Roles, useAuth } from "../../providers/AuthContext";
import {
  cancelBooking,
  createBooking,
  getAvailableConnectors,
  getAvailableConnectorDetails,
  getBooking,
  getBookingLocations,
  getBookingOverview,
  getBookingTerms,
  getMyBookings,
  updateBooking,
  updateBookingTerms,
} from "../../api/Booking";
import { getAllCustomerTokens } from "../../api/Tokens";
import { getAllCustomerWithAccessToSite, getAllCustomersForSessions } from "../../api/Customers";
import BookingTermsModal from "./BookingTermsModal";
import i18n from "../../i18n/i18n";

const toIsoStringOrNull = (value) => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toISOString();
};

const getConnectorPublicId = (connector) =>
  connector?.chargingConnectorPublicId ?? connector?.connectorPublicId ?? null;

const getConnectorName = (connector) =>
  connector?.chargingConnectorName ?? connector?.connectorName ?? i18n.t("booking.unnamedConnector");

const getConnectorStatus = (connector) =>
  connector?.chargingConnectorStatus ?? connector?.connectorStatus ?? null;

const getConnectorOcpiEvseId = (connector) =>
  connector?.ocpiEvseId ?? connector?.OcpiEvseId ?? connector?.evseUid ?? null;

const getConnectorSitePublicId = (connector, parentSitePublicId) =>
  connector?.chargingSitePublicId ?? parentSitePublicId ?? null;

const getConnectorAvailability = (connector) =>
  connector?.isAvaliable ??
  connector?.isAvailable ??
  connector?.connector?.isAvaliable ??
  connector?.connector?.isAvailable ??
  false;

const getConnectorStandard = (connector) =>
  connector?.connectorStandard ?? connector?.ConnectorStandard ?? null;

const getConnectorFormat = (connector) =>
  connector?.connectorFormat ?? connector?.ConnectorFormat ?? null;

const getConnectorPowerType = (connector) =>
  connector?.powerType ?? connector?.PowerType ?? null;

const getConnectorMaxElectricPower = (connector) =>
  connector?.maxElectricPower ?? connector?.MaxElectricPower ?? null;

const getSitePublicId = (site) =>
  site?.chargingSitePublicId ?? site?.sitePublicId ?? "unknown-site";

const getSiteName = (site, fallbackSitePublicId) =>
  site?.chargingSiteName ?? site?.siteName ?? site?.name ?? fallbackSitePublicId;

const normalizeBooking = (booking) => {
  const startRaw = booking?.startDateTimeUtc ?? null;
  const endRaw = booking?.endDateTimeUtc ?? null;
  const tokenPublicIds = Array.isArray(booking?.tokens)
    ? booking.tokens
      .map((token) => token?.tokenPublicId || token?.idTagPublicId || null)
      .filter(Boolean)
    : [];

  const bufferStartRaw =
    booking?.bufferStartDateTimeUtc ?? booking?.BufferStartDateTimeUtc ?? null;
  const bufferEndRaw =
    booking?.bufferEndDateTimeUtc ?? booking?.BufferEndDateTimeUtc ?? null;

  return {
    ...booking,
    bookingPublicId:
      booking?.bookingPublicId ||
      `${startRaw ?? "start"}-${endRaw ?? "end"}`,
    label:
      booking?.bookingPublicId ||
      i18n.t("entity.booking"),
    startDateTimeUtc: toIsoStringOrNull(startRaw),
    endDateTimeUtc: toIsoStringOrNull(endRaw),
    bufferStartDateTimeUtc: toIsoStringOrNull(bufferStartRaw),
    bufferEndDateTimeUtc: toIsoStringOrNull(bufferEndRaw),
    bookingType: booking?.bookingType || "BOOKING",
    bookingSource: booking?.bookingSource ?? booking?.BookingSource ?? null,
    customerName: booking?.customerName ?? null,
    customerPublicId: booking?.customerPublicId ?? null,
    isOwn: booking?.isOwn ?? booking?.IsOwn ?? null,
    isChargerNotified: booking?.isChargerNotified ?? booking?.IsChargerNotified,
    reservationStatus: booking?.reservationStatus ?? booking?.ReservationStatus ?? null,
    tokenPublicIds,
  };
};

const normalizeConnector = (connector, parentSitePublicId) => {
  const bookings = Array.isArray(connector?.bookings)
    ? connector.bookings
    : Array.isArray(connector?.bookingItems)
      ? connector.bookingItems
      : Array.isArray(connector?.reservations)
        ? connector.reservations
        : Array.isArray(connector?.blocks)
          ? connector.blocks
          : [];

  const hubStartRaw = connector?.hubStartBufferMinutes ?? connector?.HubStartBufferMinutes;
  const hubLateRaw = connector?.hubLateStopBufferMinutes ?? connector?.HubLateStopBufferMinutes;

  return {
    connectorPublicId: getConnectorPublicId(connector),
    ocpiEvseId: getConnectorOcpiEvseId(connector),
    connectorName: getConnectorName(connector),
    chargingConnectorStatus: getConnectorStatus(connector),
    chargingSitePublicId: getConnectorSitePublicId(connector, parentSitePublicId),
    hubStartBufferMinutes:
      hubStartRaw === null || hubStartRaw === undefined || hubStartRaw === ""
        ? null
        : Number(hubStartRaw),
    hubLateStopBufferMinutes:
      hubLateRaw === null || hubLateRaw === undefined || hubLateRaw === ""
        ? null
        : Number(hubLateRaw),
    bookings: bookings.map(normalizeBooking).sort((a, b) => {
      const aStart = new Date(a.startDateTimeUtc ?? 0).getTime();
      const bStart = new Date(b.startDateTimeUtc ?? 0).getTime();
      return aStart - bStart;
    }),
  };
};

const normalizeSite = (site) => {
  const sitePublicId = getSitePublicId(site);
  const directConnectors = Array.isArray(site?.connectors)
    ? site.connectors
    : Array.isArray(site?.chargingConnectors)
      ? site.chargingConnectors
      : [];

  const pointConnectors = Array.isArray(site?.chargingPoints)
    ? site.chargingPoints.flatMap((point) =>
        (Array.isArray(point?.chargingConnectors) ? point.chargingConnectors : []).map((connector) => ({
          ...connector,
          chargingSitePublicId: point?.chargingSitePublicId ?? point?.sitePublicId ?? sitePublicId,
        }))
      )
    : [];

  const mergedConnectors = [...directConnectors, ...pointConnectors].map((connector) =>
    normalizeConnector(connector, sitePublicId)
  );

  const byConnectorId = new Map();
  mergedConnectors.forEach((connector) => {
    const key = connector.connectorPublicId || connector.connectorName;
    const current = byConnectorId.get(key);

    if (!current) {
      byConnectorId.set(key, connector);
      return;
    }

    byConnectorId.set(key, {
      ...current,
      hubStartBufferMinutes: current.hubStartBufferMinutes ?? connector.hubStartBufferMinutes,
      hubLateStopBufferMinutes: current.hubLateStopBufferMinutes ?? connector.hubLateStopBufferMinutes,
      bookings: [...current.bookings, ...connector.bookings].sort((a, b) => {
        const aStart = new Date(a.startDateTimeUtc ?? 0).getTime();
        const bStart = new Date(b.startDateTimeUtc ?? 0).getTime();
        return aStart - bStart;
      }),
    });
  });

  return {
    sitePublicId,
    siteName: getSiteName(site, sitePublicId),
    connectors: Array.from(byConnectorId.values()).sort((a, b) =>
      (a.connectorName ?? "").localeCompare(b.connectorName ?? "")
    ),
  };
};

const normalizeAvailableConnector = (connector, parentSitePublicId) => ({
  connectorPublicId: getConnectorPublicId(connector),
  ocpiEvseId: getConnectorOcpiEvseId(connector),
  connectorName: getConnectorName(connector),
  chargingConnectorStatus: getConnectorStatus(connector),
  chargingSitePublicId: getConnectorSitePublicId(connector, parentSitePublicId),
  connectorStandard: getConnectorStandard(connector),
  connectorFormat: getConnectorFormat(connector),
  powerType: getConnectorPowerType(connector),
  maxElectricPower: getConnectorMaxElectricPower(connector),
  isAvailable: getConnectorAvailability(connector) === true,
});

const normalizeFlatOverview = (items) => {
  const siteMap = new Map();

  items.forEach((item) => {
    const site = item?.site ?? item;
    const connector = item?.connector ?? item;
    const sitePublicId = getSitePublicId(site);
    const siteName = getSiteName(site, sitePublicId);
    const connectorPublicId = getConnectorPublicId(connector) ?? "unknown-connector";
    const ocpiEvseId = getConnectorOcpiEvseId(connector);
    const connectorName = getConnectorName(connector);

    const siteEntry = siteMap.get(sitePublicId) ?? {
      sitePublicId,
      siteName,
      connectors: [],
    };

    const connectorEntry = siteEntry.connectors.find(
      (connector) => connector.connectorPublicId === connectorPublicId
    );

    const normalizedBooking = normalizeBooking(item);

    if (connectorEntry) {
      connectorEntry.bookings.push(normalizedBooking);
    } else {
      siteEntry.connectors.push({
        connectorPublicId,
        ocpiEvseId,
        connectorName,
        chargingConnectorStatus: getConnectorStatus(connector),
        bookings: [normalizedBooking],
      });
    }

    siteMap.set(sitePublicId, siteEntry);
  });

  return Array.from(siteMap.values()).map((site) => ({
    ...site,
    connectors: site.connectors
      .map((connector) => ({
        ...connector,
        bookings: connector.bookings.sort((a, b) => {
          const aStart = new Date(a.startDateTimeUtc ?? 0).getTime();
          const bStart = new Date(b.startDateTimeUtc ?? 0).getTime();
          return aStart - bStart;
        }),
      }))
      .sort((a, b) => (a.connectorName ?? "").localeCompare(b.connectorName ?? "")),
  }));
};

const extractSites = (response) => {
  const directSites =
    response?.sites ||
    response?.chargingSites ||
    response?.items ||
    response?.data ||
    response;

  if (Array.isArray(directSites)) {
    const looksNested = directSites.some(
      (site) =>
        Array.isArray(site?.connectors) ||
        Array.isArray(site?.chargingConnectors) ||
        Array.isArray(site?.chargingPoints)
    );

    if (looksNested) {
      return directSites.map(normalizeSite);
    }

    return normalizeFlatOverview(directSites);
  }

  if (Array.isArray(response?.sites)) {
    return response.sites.map(normalizeSite);
  }

  return [];
};

// Customer overview must never treat missing isOwn as "own" (would leak names/ids).
const maskForeignBookingsForCustomer = (sites) =>
  (Array.isArray(sites) ? sites : []).map((site) => ({
    ...site,
    connectors: (Array.isArray(site?.connectors) ? site.connectors : []).map((connector) => ({
      ...connector,
      bookings: (Array.isArray(connector?.bookings) ? connector.bookings : [])
        .filter((booking) => !isTimelineBookingWithoutBuffer(booking))
        .map((booking) => {
          if (booking?.isOwn === true) {
            return booking;
          }

          return {
            ...booking,
            isOwn: false,
            bookingPublicId: "",
            customerPublicId: null,
            customerName: null,
            label: "OCCUPIED",
            tokenPublicIds: [],
          };
        }),
    })),
  }));

const normalizeSearch = (value) => value.trim().toLowerCase();

const matchesSearch = (value, search) =>
  Boolean(value) && String(value).toLowerCase().includes(search);

const getSiteExpansionKey = (site) => site?.sitePublicId || site?.siteName || "unknown-site";

const formatBookingDuration = (startValue, endValue, t) => {
  const start = startValue ? new Date(startValue) : null;
  const end = endValue ? new Date(endValue) : null;

  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || end <= start) {
    return null;
  }

  const totalMinutes = differenceInMinutes(end, start);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;

  if (hours > 0 && minutes > 0) {
    return t("booking.hoursMinutes", { hours, minutes });
  }

  if (hours > 0) {
    return t("booking.hoursOnly", { hours });
  }

  return t("booking.minutesOnly", { minutes });
};

const formatMyBookingTimes = (startValue, endValue, language) => {
  const start = startValue ? new Date(startValue) : null;
  const end = endValue ? new Date(endValue) : null;

  if (!start || !end || Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return {
      dateLabel: "-",
      timeLabel: "-",
      endDateLabel: null,
    };
  }

  const sameDay = isSameDay(start, end);
  const locale = getUiDateLocale(language);
  const dateOptions = { weekday: "short", day: "numeric", month: "short", year: "numeric" };

  return {
    dateLabel: start.toLocaleDateString(locale, dateOptions),
    timeLabel: sameDay
      ? `${format(start, "HH:mm")} – ${format(end, "HH:mm")}`
      : `${format(start, "HH:mm")} → ${format(end, "HH:mm")}`,
    endDateLabel: sameDay ? null : end.toLocaleDateString(locale, dateOptions),
  };
};

const MY_BOOKING_STATUS_APPEARANCE = {
  PENDING: {
    label: "Pending",
    className: "bg-kraft-100 text-kraft-700 border-kraft-400",
  },
  RESERVED: {
    label: "Reserved",
    className: "bg-primary-100 text-primary-600 border-primary-400",
  },
  CANCELED: {
    label: "Canceled",
    className: "bg-mute-100 text-mute-700 border-mute-400",
  },
  FAILED: {
    label: "Failed",
    className: "bg-error-100 text-error-700 border-error-400",
  },
  NO_SHOW: {
    label: "No show",
    className: "bg-kraft-100 text-kraft-700 border-kraft-500",
  },
  FULFILLED: {
    label: "Completed",
    className: "bg-energi-100 text-energi-700 border-energi-500",
  },
  REJECTED: {
    label: "Rejected",
    className: "bg-error-100 text-error-700 border-error-400",
  },
};

const MY_BOOKING_TERMINAL_STATUSES = new Set([
  "CANCELED",
  "FULFILLED",
  "FAILED",
  "NO_SHOW",
  "REJECTED",
]);

const getMyBookingStatusKey = (status) => String(status ?? "").toUpperCase();

const isMyBookingTerminalStatus = (status) =>
  MY_BOOKING_TERMINAL_STATUSES.has(getMyBookingStatusKey(status));

const getMyBookingStatusAppearance = (status) => {
  const key = getMyBookingStatusKey(status);
  return (
    MY_BOOKING_STATUS_APPEARANCE[key] ?? {
      label: key ? key.replace(/_/g, " ") : "Unknown",
      className: "bg-mute-100 text-mute-700 border-mute-400",
    }
  );
};

const sortMyBookings = (bookings) =>
  [...bookings].sort((a, b) => {
    const aTerminal = isMyBookingTerminalStatus(a.reservationStatus);
    const bTerminal = isMyBookingTerminalStatus(b.reservationStatus);

    if (aTerminal !== bTerminal) {
      return aTerminal ? 1 : -1;
    }

    const aStart = new Date(a.startDateTimeUtc ?? 0).getTime();
    const bStart = new Date(b.startDateTimeUtc ?? 0).getTime();
    return aStart - bStart;
  });

const MY_BOOKING_STATUS_FILTER_OPTIONS = [
  { key: "RESERVED", label: "Reserved" },
  { key: "NO_SHOW", label: "No show" },
  { key: "CANCELED", label: "Canceled" },
  { key: "FULFILLED", label: "Completed" },
];

const DEFAULT_MY_BOOKING_STATUS_FILTERS = new Set(["RESERVED"]);

const filterMyBookingsByStatus = (bookings, selectedStatuses) => {
  if (!(selectedStatuses instanceof Set) || selectedStatuses.size === 0) {
    return [];
  }

  return bookings.filter((booking) =>
    selectedStatuses.has(getMyBookingStatusKey(booking.reservationStatus))
  );
};

const filterMyBookingsBySearch = (bookings, search, t) => {
  if (!search) {
    return bookings;
  }

  return bookings.filter((booking) => {
    const statusKey = getMyBookingStatusKey(booking.reservationStatus);
    const statusAppearance = getMyBookingStatusAppearance(booking.reservationStatus);
    const statusLabel = t
      ? t(`booking.status.${statusKey}`, { defaultValue: statusAppearance.label })
      : statusAppearance.label;
    const tokenLabels = formatMyBookingTokens(booking.tokens, t);

    return (
      matchesSearch(booking.siteName, search) ||
      matchesSearch(booking.chargingSitePublicId, search) ||
      matchesSearch(booking.chargingConnectorName, search) ||
      matchesSearch(booking.chargingConnectorPublicId, search) ||
      matchesSearch(booking.chargingPointName, search) ||
      matchesSearch(booking.chargingPointPublicId, search) ||
      matchesSearch(booking.bookingPublicId, search) ||
      matchesSearch(booking.reservationStatus, search) ||
      matchesSearch(statusLabel, search) ||
      matchesSearch(tokenLabels, search) ||
      matchesSearch((booking.tokenPublicIds ?? []).join(" "), search)
    );
  });
};

const getTokenDisplayLabel = (token, t) =>
  token?.name ||
  token?.visualNumber ||
  token?.idTagPublicId ||
  token?.tokenPublicId ||
  (t ? t("entity.token") : "Token");

const formatMyBookingTokens = (tokens, t) => {
  if (!Array.isArray(tokens) || tokens.length === 0) {
    return t ? t("booking.noTokens") : "No tokens";
  }

  return tokens.map((token) => getTokenDisplayLabel(token, t)).join(", ");
};

const formatEarlyStartLabel = (booking, t) => {
  const minutes = Number(booking?.earlyStartMinutes ?? 0);
  const fromValue = booking?.earlyStartFromUtc ?? booking?.bufferStartDateTimeUtc ?? null;
  const fromDate = fromValue ? new Date(fromValue) : null;

  if (!Number.isFinite(minutes) || minutes <= 0 || !fromDate || Number.isNaN(fromDate.getTime())) {
    return t("booking.noEarlyStart");
  }

  return t("booking.earlyStartFrom", { time: format(fromDate, "HH:mm"), minutes });
};

const filterOverviewBySearch = (sites, search, t) => {
  if (!search) {
    return sites;
  }

  const occupiedLabel = t ? t("booking.occupied") : "Occupied";

  return sites
    .map((site) => {
      const siteMatches =
        matchesSearch(site.siteName, search) ||
        matchesSearch(site.sitePublicId, search);

      const connectors = (Array.isArray(site.connectors) ? site.connectors : [])
        .map((connector) => {
          const connectorMatches =
            matchesSearch(connector.connectorName, search) ||
            matchesSearch(connector.connectorPublicId, search) ||
            matchesSearch(connector.chargingConnectorStatus, search);

          const bookings = Array.isArray(connector.bookings) ? connector.bookings : [];
          const filteredBookings = bookings.filter((booking) => {
            const isOccupiedForeign = booking?.isOwn === false;
            return (
              matchesSearch(booking.bookingPublicId, search) ||
              matchesSearch(booking.label, search) ||
              (isOccupiedForeign && matchesSearch(occupiedLabel, search)) ||
              matchesSearch(booking.bookingType, search) ||
              matchesSearch(booking.customerName, search) ||
              matchesSearch(booking.reservationStatus, search) ||
              matchesSearch((booking.tokenPublicIds ?? []).join(" "), search)
            );
          });

          if (!siteMatches && !connectorMatches && filteredBookings.length === 0) {
            return null;
          }

          return {
            ...connector,
            bookings: siteMatches || connectorMatches ? bookings : filteredBookings,
          };
        })
        .filter(Boolean);

      if (!siteMatches && connectors.length === 0) {
        return null;
      }

      return {
        ...site,
        connectors,
      };
    })
    .filter(Boolean);
};

const BookingPage = () => {
  const { t, i18n } = useTranslation();
  const { getAccessToken, hasRole, hasAnyRole } = useAuth();
  const isCustomerUser = hasRole(Roles.Customer);
  const showCanceledAndNoShowLegend = hasAnyRole([Roles.Admin, Roles.CpmsLight]);
  const defaultStartDate = useMemo(() => startOfDay(new Date()), []);
  const defaultEndDate = useMemo(() => endOfDay(addDays(new Date(), 1)), []);
  const [loading, setLoading] = useState(true);
  const [bookingOverview, setBookingOverview] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [myBookingsExpanded, setMyBookingsExpanded] = useState(true);
  const [myBookingStatusFilters, setMyBookingStatusFilters] = useState(
    () => new Set(DEFAULT_MY_BOOKING_STATUS_FILTERS)
  );
  const [expandedSites, setExpandedSites] = useState({});
  const [search, setSearch] = useState("");
  const [bookingLocationByConnectorId, setBookingLocationByConnectorId] = useState({});
  const [startDate, setStartDate] = useState(defaultStartDate);
  const [endDate, setEndDate] = useState(defaultEndDate);
  const [appliedStartDate, setAppliedStartDate] = useState(defaultStartDate);
  const [appliedEndDate, setAppliedEndDate] = useState(defaultEndDate);
  const [wizardState, setWizardState] = useState({
    isOpen: false,
    mode: "create",
    context: null,
  });
  const [wizardSubmitting, setWizardSubmitting] = useState(false);
  const [termsState, setTermsState] = useState({
    isOpen: false,
    connector: null,
    bookingLocationPublicId: null,
  });
  const [termsLoading, setTermsLoading] = useState(false);
  const [termsSaving, setTermsSaving] = useState(false);
  const [terms, setTerms] = useState(null);

  const loadBookingLocationMap = async (accessToken) => {
    const locations = await getBookingLocations(accessToken);
    const list = Array.isArray(locations) ? locations : Array.isArray(locations?.items) ? locations.items : [];

    return list.reduce((acc, item) => {
      const connectorId = item?.chargingConnectorPublicId || null;
      const bookingLocationPublicId = item?.bookingLocationPublicId || null;

      if (!connectorId || !bookingLocationPublicId) {
        return acc;
      }

      acc[connectorId] = {
        bookingLocationPublicId,
        hasCustomBookingTerms: item?.hasCustomBookingTerms === true,
      };
      return acc;
    }, {});
  };

  const loadOverview = async (rangeStart, rangeEnd) => {
    const accessToken = await getAccessToken();
    const [resp, locationMap] = await Promise.all([
      getBookingOverview(
        accessToken,
        toIsoStringOrNull(rangeStart),
        toIsoStringOrNull(rangeEnd)
      ),
      loadBookingLocationMap(accessToken).catch((error) => {
        console.error(error);
        return {};
      }),
    ]);

    const normalizedSites = isCustomerUser
      ? maskForeignBookingsForCustomer(extractSites(resp))
      : extractSites(resp);
    setBookingOverview(normalizedSites);
    setBookingLocationByConnectorId(locationMap);
    setExpandedSites((current) =>
      normalizedSites.reduce((acc, site) => {
        const key = getSiteExpansionKey(site);
        acc[key] = current[key] ?? false;
        return acc;
      }, {})
    );

    if (isCustomerUser) {
      const mine = await getMyBookings(
        accessToken,
        toIsoStringOrNull(rangeStart),
        toIsoStringOrNull(rangeEnd)
      );
      setMyBookings(
        Array.isArray(mine)
          ? sortMyBookings(
              mine.map((item) => ({
                ...normalizeBooking(item),
                siteName: item?.siteName ?? item?.SiteName ?? null,
                chargingConnectorName:
                  item?.chargingConnectorName ?? item?.ChargingConnectorName ?? null,
                chargingPointName: item?.chargingPointName ?? item?.ChargingPointName ?? null,
                chargingPointPublicId:
                  item?.chargingPointPublicId ?? item?.ChargingPointPublicId ?? null,
                chargingSitePublicId:
                  item?.chargingSitePublicId ?? item?.ChargingSitePublicId ?? null,
                chargingConnectorPublicId:
                  item?.chargingConnectorPublicId ?? item?.ChargingConnectorPublicId ?? null,
                earlyStartMinutes: item?.earlyStartMinutes ?? item?.EarlyStartMinutes ?? 0,
                earlyStartFromUtc:
                  item?.earlyStartFromUtc ?? item?.EarlyStartFromUtc ?? null,
                tokens: Array.isArray(item?.tokens)
                  ? item.tokens
                  : Array.isArray(item?.Tokens)
                    ? item.Tokens
                    : [],
                isOwn: true,
              }))
            )
          : []
      );
    } else {
      setMyBookings([]);
    }
  };

  const visibleSites = useMemo(() => {
    return filterOverviewBySearch(bookingOverview, normalizeSearch(search), t);
  }, [bookingOverview, search, t]);

  const visibleMyBookings = useMemo(() => {
    const byStatus = filterMyBookingsByStatus(myBookings, myBookingStatusFilters);
    return filterMyBookingsBySearch(byStatus, normalizeSearch(search), t);
  }, [myBookings, myBookingStatusFilters, search, t]);

  const toggleMyBookingStatusFilter = (statusKey) => {
    setMyBookingStatusFilters((current) => {
      const next = new Set(current);
      if (next.has(statusKey)) {
        next.delete(statusKey);
      } else {
        next.add(statusKey);
      }
      return next;
    });
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        await loadOverview(appliedStartDate, appliedEndDate);
      } catch (error) {
        console.error(error);
        toast.error(error?.message || t("booking.failedLoadOverview"));
        setBookingOverview([]);
        setMyBookings([]);
        setBookingLocationByConnectorId({});
        setExpandedSites({});
      } finally {
        setLoading(false);
      }
    };

    if (appliedStartDate && appliedEndDate) {
      load();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, [appliedEndDate, appliedStartDate, getAccessToken, isCustomerUser]);

  useEffect(() => {
    if (startDate && endDate && endDate < startDate) {
      setEndDate(startDate);
    }
  }, [startDate, endDate]);

  const handleSearch = (event) => {
    event?.preventDefault();

    if (!startDate || !endDate) {
      return;
    }

    setLoading(true);
    setAppliedStartDate(new Date(startDate));
    setAppliedEndDate(new Date(endDate));
  };

  const handleApplyRange = (nextStartDate, nextEndDate) => {
    if (!nextStartDate || !nextEndDate) {
      return;
    }

    const start = new Date(nextStartDate);
    const end = new Date(nextEndDate);

    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
      return;
    }

    setLoading(true);
    setStartDate(start);
    setEndDate(end);
    setAppliedStartDate(start);
    setAppliedEndDate(end);
  };

  const handleOpenCreateWizard = (site = null) => {
    setWizardState({
      isOpen: true,
      mode: "create",
      context: site ? { site } : null,
    });
  };

  const handleOpenEditWizard = (context) => {
    if (isCustomerUser && context?.booking?.isOwn !== true) {
      return;
    }

    setWizardState({
      isOpen: true,
      mode: "edit",
      context,
    });
  };

  const handleCloseWizard = () => {
    if (wizardSubmitting) {
      return;
    }

    setWizardState({
      isOpen: false,
      mode: "create",
      context: null,
    });
  };

  const handleLoadAvailableConnectors = async (sitePublicId, startValue, endValue, excludeBookingPublicId, bookingType) => {
    const accessToken = await getAccessToken();
    const response = await getAvailableConnectors(
      sitePublicId,
      accessToken,
      toIsoStringOrNull(startValue),
      toIsoStringOrNull(endValue),
      excludeBookingPublicId,
      bookingType
    );
    const rawConnectors = Array.isArray(response)
      ? response
      : Array.isArray(response?.connectors)
        ? response.connectors
        : Array.isArray(response?.items)
          ? response.items
          : [];

    return rawConnectors
      .map((connector) => normalizeAvailableConnector(connector, sitePublicId))
      .filter((connector) => connector.isAvailable === true);
  };

  const handleLoadConnectorDetails = async (connectorPublicId) => {
    const accessToken = await getAccessToken();
    return getAvailableConnectorDetails(connectorPublicId, accessToken);
  };

  const handleLoadCustomers = async (sitePublicId) => {
    const accessToken = await getAccessToken();
    if (isCustomerUser) {
      return getAllCustomersForSessions(accessToken);
    }

    return getAllCustomerWithAccessToSite(sitePublicId, accessToken);
  };

  const handleOpenMyBooking = (booking) => {
    if (!booking?.bookingPublicId) {
      return;
    }

    handleOpenEditWizard({
      booking: {
        ...normalizeBooking(booking),
        isOwn: true,
        chargingSitePublicId: booking.chargingSitePublicId ?? booking.ChargingSitePublicId ?? null,
        chargingConnectorPublicId:
          booking.chargingConnectorPublicId ?? booking.ChargingConnectorPublicId ?? null,
      },
      site: {
        sitePublicId: booking.chargingSitePublicId ?? booking.ChargingSitePublicId ?? null,
        siteName: booking.siteName ?? booking.SiteName ?? null,
      },
      connector: {
        connectorPublicId:
          booking.chargingConnectorPublicId ?? booking.ChargingConnectorPublicId ?? null,
        connectorName:
          booking.chargingConnectorName ?? booking.ChargingConnectorName ?? null,
        chargingSitePublicId: booking.chargingSitePublicId ?? booking.ChargingSitePublicId ?? null,
      },
    });
  };

  const handleLoadTokens = async (customerPublicId) => {
    const accessToken = await getAccessToken();
    return getAllCustomerTokens(customerPublicId, accessToken);
  };

  const handleLoadBookingDetails = async (bookingPublicId) => {
    const accessToken = await getAccessToken();
    const response = await getBooking(bookingPublicId, accessToken);
    return normalizeBooking(response);
  };

  const handleLoadBookingTermsForConnector = async (connectorPublicId) => {
    const location = connectorPublicId ? bookingLocationByConnectorId[connectorPublicId] : null;
    const bookingLocationPublicId = location?.bookingLocationPublicId ?? null;

    if (!bookingLocationPublicId) {
      return null;
    }

    const accessToken = await getAccessToken();
    return getBookingTerms(bookingLocationPublicId, accessToken);
  };

  const handleCreateBooking = async (payload) => {
    setWizardSubmitting(true);
    const toastId = toast.loading(t("booking.creating"));

    try {
      const accessToken = await getAccessToken();
      const createPayload = isCustomerUser
        ? {
            ...payload,
            bookingType: "BOOKING",
          }
        : payload;
      await createBooking(createPayload, accessToken);
      toast.success(t("booking.created"), { id: toastId });
      await loadOverview(appliedStartDate, appliedEndDate);
      handleCloseWizard();
    } catch (error) {
      console.error(error);
      toast.error(error?.message || t("booking.failedCreate"), { id: toastId });
    } finally {
      setWizardSubmitting(false);
    }
  };

  const handleUpdateBooking = async (bookingPublicId, payload) => {
    setWizardSubmitting(true);

    try {
      const accessToken = await getAccessToken();
      const updatePayload = isCustomerUser
        ? {
            ...payload,
            bookingType: "BOOKING",
          }
        : payload;
      await updateBooking(bookingPublicId, updatePayload, accessToken);
      toast.success(t("booking.updated"));
      await loadOverview(appliedStartDate, appliedEndDate);
      handleCloseWizard();
    } catch (error) {
      console.error(error);
      toast.error(error?.message || t("booking.failedUpdate"));
    } finally {
      setWizardSubmitting(false);
    }
  };

  const handleCancelBooking = async (bookingPublicId, payload) => {
    setWizardSubmitting(true);

    try {
      const accessToken = await getAccessToken();
      await cancelBooking(bookingPublicId, payload, accessToken);
      toast.success(t("booking.canceled"));
      await loadOverview(appliedStartDate, appliedEndDate);
      handleCloseWizard();
    } catch (error) {
      console.error(error);
      toast.error(error?.message || t("booking.failedCancel"));
    } finally {
      setWizardSubmitting(false);
    }
  };

  const handleOpenTerms = async ({ connector }) => {
    const connectorPublicId = connector?.connectorPublicId ?? connector?.chargingConnectorPublicId ?? null;
    const location = connectorPublicId ? bookingLocationByConnectorId[connectorPublicId] : null;
    const bookingLocationPublicId = location?.bookingLocationPublicId ?? null;

    setTermsState({
      isOpen: true,
      connector,
      bookingLocationPublicId,
    });

    if (!bookingLocationPublicId) {
      setTerms(null);
      return;
    }

    setTermsLoading(true);
    try {
      const accessToken = await getAccessToken();
      const fetched = await getBookingTerms(bookingLocationPublicId, accessToken);
      setTerms(fetched ?? null);
    } catch (error) {
      console.error(error);
      toast.error(error?.message || t("booking.failedLoadTerms"));
      setTerms(null);
    } finally {
      setTermsLoading(false);
    }
  };

  const handleCloseTerms = () => {
    if (termsSaving) {
      return;
    }

    setTermsState({
      isOpen: false,
      connector: null,
      bookingLocationPublicId: null,
    });
    setTerms(null);
    setTermsLoading(false);
  };

  const handleSaveTerms = async (payload) => {
    setTermsSaving(true);
    const toastId = toast.loading(t("booking.savingTerms"));
    try {
      const accessToken = await getAccessToken();
      const result = await updateBookingTerms(payload, accessToken);
      setTerms(result ?? payload);
      toast.success(t("booking.termsSaved"), { id: toastId });
      const map = await loadBookingLocationMap(accessToken);
      setBookingLocationByConnectorId(map);
    } catch (error) {
      console.error(error);
      toast.error(error?.message || t("booking.failedSaveTerms"), { id: toastId });
      throw error;
    } finally {
      setTermsSaving(false);
    }
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">
            <div className="page-content">
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-black">
                      {isCustomerUser ? t("booking.bookings") : t("booking.overview")}
                    </h2>
                    {isCustomerUser && (
                      <p className="mt-1 text-sm text-mute-500">
                        {t("booking.customerIntro")}
                      </p>
                    )}
                  </div>

                  <BookingLegend showCanceledAndNoShow={showCanceledAndNoShowLegend} />
                </div>

                <BookingFilters
                  search={search}
                  setSearch={setSearch}
                  startDate={startDate}
                  endDate={endDate}
                  setStartDate={setStartDate}
                  setEndDate={setEndDate}
                  onQuickRangeSelect={handleApplyRange}
                  onSearch={handleSearch}
                  isSearching={loading}
                />

                {loading ? (
                  <div className="rounded-2xl border border-mute-200 bg-white px-6 py-16">
                    <LoadingSpinner text={t("booking.loading")} />
                  </div>
                ) : (
                  <>
                    {isCustomerUser && (
                      <div className="rounded-2xl border border-mute-200 bg-white shadow-lg">
                        <button
                          type="button"
                          className="flex w-full items-center justify-between gap-3 px-4 py-4 text-left sm:px-6"
                          onClick={() => setMyBookingsExpanded((current) => !current)}
                          aria-expanded={myBookingsExpanded}
                        >
                          <div className="min-w-0">
                            <h3 className="text-lg font-bold text-black">{t("booking.myBookings")}</h3>
                            <p className="mt-0.5 text-sm text-mute-500">
                              {myBookings.length > visibleMyBookings.length
                                ? t("booking.shownOfRange", { shown: visibleMyBookings.length, total: myBookings.length })
                                : t("booking.shownInRange", { count: visibleMyBookings.length })}
                            </p>
                          </div>
                          <FontAwesomeIcon
                            icon={myBookingsExpanded ? faChevronUp : faChevronDown}
                            className="shrink-0 text-mute-500"
                          />
                        </button>

                        {myBookingsExpanded && (
                          <div className="border-t border-mute-200 px-4 py-4 sm:px-6">
                            <div className="mb-4 flex flex-wrap items-center gap-2">
                              <span className="text-xs font-semibold uppercase tracking-wide text-mute-500">
                                {t("common.status")}
                              </span>
                              {MY_BOOKING_STATUS_FILTER_OPTIONS.map((option) => {
                                const isSelected = myBookingStatusFilters.has(option.key);
                                const appearance = getMyBookingStatusAppearance(option.key);

                                return (
                                  <button
                                    key={option.key}
                                    type="button"
                                    aria-pressed={isSelected}
                                    onClick={() => toggleMyBookingStatusFilter(option.key)}
                                    className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold transition ${
                                      isSelected
                                        ? appearance.className
                                        : "border-mute-300 bg-white text-mute-500 hover:border-mute-400"
                                    }`}
                                  >
                                    {t(`booking.status.${option.key}`)}
                                  </button>
                                );
                              })}
                            </div>

                            {myBookings.length === 0 ? (
                              <p className="text-sm text-mute-500">{t("booking.noBookingsInInterval")}</p>
                            ) : visibleMyBookings.length === 0 ? (
                              <p className="text-sm text-mute-500">
                                {normalizeSearch(search)
                                  ? t("booking.noMatchSearchStatus")
                                  : t("booking.noMatchStatus")}
                              </p>
                            ) : (
                              <div className="flex flex-col gap-4">
                                {visibleMyBookings.map((booking) => {
                                  const times = formatMyBookingTimes(
                                    booking.startDateTimeUtc,
                                    booking.endDateTimeUtc,
                                    i18n.resolvedLanguage || i18n.language
                                  );
                                  const duration = formatBookingDuration(
                                    booking.startDateTimeUtc,
                                    booking.endDateTimeUtc,
                                    t
                                  );
                                  const statusAppearance = getMyBookingStatusAppearance(
                                    booking.reservationStatus
                                  );
                                  const isTerminal = isMyBookingTerminalStatus(
                                    booking.reservationStatus
                                  );
                                  const showConfirmed =
                                    !isTerminal && booking.isChargerNotified === true;
                                  const chargingPointName =
                                    booking.chargingPointName || booking.chargingPointPublicId || null;

                                  return (
                                    <div
                                      key={booking.bookingPublicId}
                                      className={`grid grid-cols-1 items-stretch gap-2 rounded-xl border px-3 py-2.5 shadow-sm sm:grid-cols-[minmax(0,1.4fr)_6.5rem_minmax(9.5rem,0.75fr)_minmax(0,1.2fr)_5.75rem] sm:gap-x-0 sm:gap-y-0 ${
                                        isTerminal
                                          ? "border-mute-200 bg-mute-50/40 opacity-90"
                                          : "border-mute-300 bg-mute-50/60"
                                      }`}
                                    >
                                      <div className="flex min-w-0 flex-col justify-center sm:pr-4">
                                        <div className="truncate font-semibold text-black">
                                          {booking.siteName || booking.chargingSitePublicId || t("entity.site")}
                                        </div>
                                        <div className="truncate text-sm text-mute-600">
                                          {booking.chargingConnectorName ||
                                            booking.chargingConnectorPublicId ||
                                            t("entity.connector")}
                                          {chargingPointName ? ` · ${chargingPointName}` : ""}
                                        </div>
                                      </div>

                                      <div className="flex w-full flex-col items-center justify-center gap-0.5 sm:border-l sm:border-mute-200 sm:px-2">
                                        <span
                                          className={`inline-flex w-full max-w-[5.75rem] items-center justify-center rounded-full border px-2 py-px text-center text-xs font-semibold ${statusAppearance.className}`}
                                        >
                                          {t(`booking.status.${getMyBookingStatusKey(booking.reservationStatus)}`, { defaultValue: statusAppearance.label })}
                                        </span>
                                        {showConfirmed && (
                                          <span className="inline-flex w-full max-w-[5.75rem] items-center justify-center rounded-full border border-energi-500 bg-energi-200 px-2 py-px text-center text-xs font-semibold text-primary-500">
                                            {t("booking.confirmed")}
                                          </span>
                                        )}
                                      </div>

                                      <div className="flex min-w-0 flex-col justify-center leading-snug sm:border-l sm:border-mute-200 sm:pl-4">
                                        <div className="text-sm font-medium text-black">
                                          {times.dateLabel}
                                          {times.endDateLabel ? ` → ${times.endDateLabel}` : ""}
                                        </div>
                                        <div className="text-sm font-semibold tabular-nums text-black">
                                          {times.timeLabel}
                                          {duration ? (
                                            <span className="ml-1.5 text-xs font-normal text-mute-500">
                                              {duration}
                                            </span>
                                          ) : null}
                                        </div>
                                      </div>

                                      <div className="flex min-w-0 flex-col justify-center leading-snug sm:border-l sm:border-mute-200 sm:px-4">
                                        <div className="text-sm text-mute-700">
                                          {formatEarlyStartLabel(booking, t)}
                                        </div>
                                        <div className="truncate text-sm text-mute-700">
                                          {t("booking.tokensLabel", { tokens: formatMyBookingTokens(booking.tokens, t) })}
                                        </div>
                                      </div>

                                      <div className="flex items-center justify-end sm:border-l sm:border-mute-200 sm:pl-2">
                                        {!isTerminal && (
                                          <button
                                            type="button"
                                            className="btn-sm btn-outline-primary justify-center"
                                            onClick={() => handleOpenMyBooking(booking)}
                                          >
                                            <FontAwesomeIcon icon={faPenToSquare} />
                                            {t("common.edit")}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div className="text-sm text-mute-500">
                        {t("booking.showingSites", { count: visibleSites.length })}
                      </div>

                      <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                        <button
                          type="button"
                          className="btn-sm btn-primary w-full justify-center sm:w-auto"
                          onClick={handleOpenCreateWizard}
                        >
                          {wizardSubmitting && wizardState.mode === "create" ? (
                            <FontAwesomeIcon icon={faSpinnerThird} spin />
                          ) : (
                            <FontAwesomeIcon icon={faCalendarPlus} />
                          )}
                          {t("booking.newBooking")}
                        </button>
                        <button
                          type="button"
                          className="btn-sm btn-outline-primary w-full justify-center sm:w-auto"
                          onClick={() =>
                            setExpandedSites((current) =>
                              visibleSites.reduce((acc, site) => {
                                acc[getSiteExpansionKey(site)] = true;
                                return acc;
                              }, { ...current })
                            )
                          }
                        >
                          {t("booking.expandAll")}
                        </button>
                        <button
                          type="button"
                          className="btn-sm btn-outline-primary w-full justify-center sm:w-auto"
                          onClick={() =>
                            setExpandedSites((current) =>
                              visibleSites.reduce((acc, site) => {
                                acc[getSiteExpansionKey(site)] = false;
                                return acc;
                              }, { ...current })
                            )
                          }
                        >
                          {t("booking.collapseAll")}
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-col gap-4">
                      {visibleSites.map((site) => (
                        <SiteCard
                          key={getSiteExpansionKey(site)}
                          site={site}
                          expanded={expandedSites[getSiteExpansionKey(site)] ?? false}
                          onToggle={() =>
                            setExpandedSites((current) => ({
                              ...current,
                              [getSiteExpansionKey(site)]: !(current[getSiteExpansionKey(site)] ?? false),
                            }))
                          }
                          onCreateBooking={handleOpenCreateWizard}
                          viewStartDate={appliedStartDate}
                          viewEndDate={appliedEndDate}
                          onBookingClick={handleOpenEditWizard}
                          onTermsClick={handleOpenTerms}
                          getTermsLabel={({ connector }) => {
                            const connectorPublicId =
                              connector?.connectorPublicId ?? connector?.chargingConnectorPublicId ?? null;
                            const location = connectorPublicId ? bookingLocationByConnectorId[connectorPublicId] : null;
                            return location?.hasCustomBookingTerms === true ? t("booking.termsCustom") : t("booking.terms");
                          }}
                          isTermsDisabled={({ connector }) => {
                            const connectorPublicId =
                              connector?.connectorPublicId ?? connector?.chargingConnectorPublicId ?? null;
                            const location = connectorPublicId ? bookingLocationByConnectorId[connectorPublicId] : null;
                            return !location?.bookingLocationPublicId;
                          }}
                        />
                      ))}

                      {!visibleSites.length && (
                        <div className="empty-state p-10 text-center text-sm">
                          {t("booking.noData")}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            <BookingWizardModal
              isOpen={wizardState.isOpen}
              onClose={handleCloseWizard}
              mode={wizardState.mode}
              sites={bookingOverview}
              bookingContext={wizardState.context}
              defaultStartDate={appliedStartDate}
              defaultEndDate={appliedEndDate}
              loadAvailableConnectors={handleLoadAvailableConnectors}
              loadConnectorDetails={handleLoadConnectorDetails}
              loadCustomers={handleLoadCustomers}
              loadTokens={handleLoadTokens}
              loadBookingDetails={handleLoadBookingDetails}
              loadBookingTermsForConnector={handleLoadBookingTermsForConnector}
              onCreate={handleCreateBooking}
              onUpdate={handleUpdateBooking}
              onCancelBooking={handleCancelBooking}
              isSubmitting={wizardSubmitting}
              isCustomerUser={isCustomerUser}
            />

            <BookingTermsModal
              isOpen={termsState.isOpen}
              onClose={handleCloseTerms}
              connectorLabel={termsState.connector?.connectorName ?? termsState.connector?.chargingConnectorName ?? null}
              bookingLocationPublicId={termsState.bookingLocationPublicId}
              terms={terms}
              loading={termsLoading}
              saving={termsSaving}
              onSave={isCustomerUser ? null : handleSaveTerms}
              readOnly={isCustomerUser}
            />
          </main>
        </div>
      </div>
    </>
  );
};

export default BookingPage;
